import { useState, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/hooks/useTranslation';
import { Button, TextInput, Textarea } from '@components/ui';
import FullPageStepper from '@components/shared/FullPageStepper/FullPageStepper';
import type { FullPageStepperStep } from '@components/shared/FullPageStepper/FullPageStepper';
import { createRole, updateRole, getRoleById } from '@/services/roleService';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import useMultiStepForm from '@/hooks/useMultiStepForm';
import { useModulesWithPermissions } from '@hooks/useModulesWithPermissions';
import PermissionPicker from '@features/roles/PermissionPicker';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './add.module.scss';

// ============================================
// ROUTE
// ============================================

interface RoleAddSearch {
  id?: string;
  cloneId?: string;
}

export const Route = createFileRoute('/w/roles/add')({
  staticData: { hideShell: true },
  validateSearch: (search: Record<string, unknown>): RoleAddSearch => ({
    id: typeof search.id === 'string' ? search.id : undefined,
    cloneId: typeof search.cloneId === 'string' ? search.cloneId : undefined,
  }),
  component: RoleAddPage,
});

// ============================================
// TYPES
// ============================================

interface RoleFormValues {
  name: string;
  description: string;
}

const DEFAULT_VALUES: RoleFormValues = {
  name: '',
  description: '',
};

// ============================================
// COMPONENT
// ============================================

function RoleAddPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  // --- Mode detection ---
  const { id: roleId, cloneId } = Route.useSearch();
  const isEditMode = !!roleId;
  const isCloneMode = !!cloneId;
  const sourceRoleId = roleId ?? cloneId;

  // --- Role query (edit or clone mode) ---
  const { data: existingRole, isLoading: roleLoading } = useQuery({
    queryKey: ['role', sourceRoleId],
    queryFn: () => getRoleById(sourceRoleId!),
    enabled: !!sourceRoleId,
  });

  // Shared with PermissionPicker — single source of truth for the perms query.
  const { error: modulesError } = useModulesWithPermissions();

  // --- Step 1: Form ---
  const {
    register,
    trigger,
    getValues,
    reset: resetForm,
    formState: { errors },
  } = useForm<RoleFormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  // --- Step 2: Permissions ---
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<string>>(new Set());

  // Pre-fill in edit/clone mode
  useEffect(() => {
    if (existingRole) {
      resetForm({
        name: isCloneMode
          ? `${t('K_COPY_OF', 'Copy of')} ${existingRole.name}`
          : existingRole.name,
        description: existingRole.description ?? '',
      });
      setSelectedPermissionIds(new Set(existingRole.permissions.map((p) => p.id)));
    }
  }, [existingRole, resetForm, isCloneMode, t]);

  // --- Multi-step form ---
  const stepper = useMultiStepForm({
    totalSteps: 2,
    onValidateStep: async (stepIndex) => {
      if (stepIndex === 0) {
        return trigger(['name']);
      }
      return true;
    },
  });

  // --- Steps config ---
  const steps: FullPageStepperStep[] = useMemo(
    () => [
      {
        id: 'details',
        label: t('K_STEP_ROLE_DETAILS', 'Role Details'),
        title: t('K_STEP_ROLE_DETAILS', 'Role Details'),
        description: t('K_STEP_ROLE_DETAILS_DESC', 'Enter the role name and description'),
      },
      {
        id: 'permissions',
        label: t('K_STEP_PERMISSIONS', 'Permissions'),
        title: t('K_STEP_PERMISSIONS', 'Permissions'),
        description: t('K_STEP_PERMISSIONS_DESC', 'Select the permissions for this role'),
      },
    ],
    [t],
  );

  // --- Mutations ---
  const createMutation = useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void navigate({ to: '/w/roles' });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: useCallback(
      (data: { name: string; description: string; permissionIds: string[] }) =>
        updateRole(roleId!, data),
      [roleId],
    ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role', roleId] });
      void navigate({ to: '/w/roles' });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // --- Navigation ---
  const handleClose = useCallback(() => {
    void navigate({ to: '/w/roles' });
  }, [navigate]);

  const handleSave = useCallback(() => {
    if (selectedPermissionIds.size === 0) {
      addNotification({
        variant: 'critical',
        title: t('K_VALIDATION_MIN_ONE_PERMISSION', 'Select at least one permission'),
      });
      return;
    }

    const formValues = getValues();
    const payload = {
      name: formValues.name,
      description: formValues.description,
      permissionIds: Array.from(selectedPermissionIds),
    };

    if (isEditMode && !isCloneMode) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  }, [selectedPermissionIds, getValues, isEditMode, isCloneMode, createMutation, updateMutation, addNotification, t]);

  // --- Footer ---
  const footer = useMemo(() => {
    if (stepper.currentStep === 0) {
      return (
        <div className={styles.footer}>
          <Button variant="outline" size="md" onClick={handleClose}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button variant="filled" size="md" onClick={() => void stepper.goToNext()}>
            {t('K_NEXT', 'Next')}
          </Button>
        </div>
      );
    }
    return (
      <div className={styles.footer}>
        <Button variant="outline" size="md" onClick={stepper.goToPrev}>
          {t('K_BACK', 'Back')}
        </Button>
        <Button
          variant="filled"
          size="md"
          onClick={handleSave}
          disabled={isSaving || selectedPermissionIds.size === 0}
          loading={isSaving}
        >
          {t('K_SAVE', 'Save')}
        </Button>
      </div>
    );
  }, [stepper, handleClose, handleSave, isSaving, selectedPermissionIds.size, t]);

  // --- Page title ---
  const pageTitle = isEditMode
    ? t('K_MODAL_EDIT_ROLE', 'Edit Role')
    : isCloneMode
      ? t('K_MODAL_CLONE_ROLE', 'Clone Role')
      : t('K_MODAL_CREATE_ROLE', 'Create Role');

  // --- Forbidden (page-swap — without permissions reference data, the role
  //     can't be created at all). Renders inside the same FullPageStepper shell
  //     so chrome behaves consistently.
  if (isForbiddenError(modulesError)) {
    return (
      <FullPageStepper
        pageTitle={pageTitle}
        steps={steps}
        activeStep={0}
        onClose={handleClose}
      >
        <Forbidden
          variant="page"
          onAction={() => void navigate({ to: '/w/roles' })}
          actionLabel={t('K_BUTTON_GO_BACK', 'Go Back')}
        />
      </FullPageStepper>
    );
  }

  // --- Loading state (edit/clone mode) ---
  if (sourceRoleId && roleLoading) {
    return (
      <FullPageStepper
        pageTitle={pageTitle}
        steps={steps}
        activeStep={0}
        onClose={handleClose}
      >
        <p>{t('K_LOADING', 'Loading...')}</p>
      </FullPageStepper>
    );
  }

  return (
    <FullPageStepper
      pageTitle={pageTitle}
      steps={steps}
      activeStep={stepper.currentStep}
      onClose={handleClose}
      onStepClick={(stepIndex: number) => stepper.goToStep(stepIndex)}
      footer={footer}
    >
      {/* ---- Step 1: Role Details ---- */}
      {stepper.currentStep === 0 && (
        <div className={styles.step1Content}>
          <div className={styles.fieldGroup}>
            <TextInput
              label={t('K_LABEL_ROLE_NAME', 'Role name')}
              isRequired
              placeholder={t('K_PLACEHOLDER_ROLE_NAME', 'Enter role name')}
              error={errors.name?.message}
              {...register('name', {
                required: t('K_VALIDATION_ROLE_NAME_REQUIRED', 'Role name is required'),
                maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
              })}
            />
          </div>
          <div className={styles.fieldGroup}>
            <Textarea
              label={t('K_LABEL_ROLE_DESCRIPTION', 'Description')}
              placeholder={t('K_PLACEHOLDER_ROLE_DESCRIPTION', 'Enter role description')}
              rows={3}
              {...register('description', {
                maxLength: { value: 500, message: t('K_VALIDATION_MAX_500', 'Max 500 characters') },
              })}
            />
          </div>
        </div>
      )}

      {/* ---- Step 2: Permissions ---- */}
      {stepper.currentStep === 1 && (
        <PermissionPicker
          selectedIds={selectedPermissionIds}
          onChange={setSelectedPermissionIds}
          enabled
        />
      )}
    </FullPageStepper>
  );
}
